import { Phone, Mail, Clock } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { ContactForm } from "../components/ContactForm";
import { SocialLinks } from "../components/SocialLinks";
import { site } from "../data/site";
import { useLang } from "../i18n";

export default function Contact() {
  const { t } = useLang();
  useSeo({
    title: "Contact & Request Information | Amenity Health Services",
    description:
      "Contact Amenity Health Services in Houston, TX. Call, email, or send a privacy-conscious request. For emergencies, call 911.",
  });

  return (
    <>
      <PageHeader
        label="Contact"
        titleLines={["Let's start", "a conversation"]}
        intro="Have questions about our services or insurance? Contact us today via phone, email, or the form below, and our team will be happy to assist."
      />

      <section className="py-10 sm:py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
          {/* Info column */}
          <Reveal className="lg:col-span-2 space-y-4">
            <div className="rounded-3xl border border-black/5 bg-white p-7 space-y-5">
              <a href={site.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal shrink-0"><Phone size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-ink/45">{t("Call us")}</span>
                  <span className="font-heading font-semibold text-ink group-hover:text-teal transition-colors">{site.phone}</span>
                </span>
              </a>
              <a href={`mailto:${site.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal shrink-0"><Mail size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-ink/45">{t("Email")}</span>
                  <span className="font-heading font-semibold text-ink group-hover:text-teal transition-colors break-all">{site.email}</span>
                </span>
              </a>
              <div className="pt-1">
                <span className="block text-xs uppercase tracking-widest text-ink/45 mb-3">{t("Follow us")}</span>
                <SocialLinks variant="light" />
              </div>
            </div>

            <div className="rounded-3xl border border-black/5 bg-white p-7">
              <h3 className="font-heading font-semibold text-lg text-ink mb-4 flex items-center gap-2">
                <Clock size={18} className="text-teal" /> {t("Hours")}
              </h3>
              <ul className="space-y-3">
                {site.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between text-sm text-ink/70 border-b border-black/5 pb-2.5 last:border-0">
                    <span>{t(h.day)}</span>
                    <span className="font-medium text-ink">{t(h.time)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Form column */}
          <Reveal className="lg:col-span-3" delay={0.1}>
            <div className="rounded-[2rem] border border-black/5 bg-cream p-7 sm:p-10">
              <h2 className="font-heading font-semibold text-2xl text-ink mb-6">{t("Request information")}</h2>
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
